import { useSuspenseQuery } from '@apollo/client/react';
import { LayoutGrid, Plus } from 'lucide-react';
import { Navigate, Link } from 'react-router';

import { Main } from 'src/components/Layout';
import { CompanyDocument } from 'src/graphql/actions/company/company.query';
import { IntegrationsDocument } from 'src/graphql/actions/integration/integrations.query';
import { withSuspense } from 'src/hoc';
import { usePageMeta } from 'src/hooks';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Button } from '@/components/ui/button';
import {
  StaticIntegrationsDialog,
  IntegrationContextProvider,
  IntegrationSetupDialog,
  DisconnectDialog,
  IntegrationTable,
  UpdateCustomIntegrationDialog,
  ReconnectOauthDialog,
  SetupAlertDialog,
} from 'src/containers/Integrations';
import { apolloCache as cache } from 'src/graphql';

import type { ICompanyQuery } from 'src/graphql/actions/company/company.query';

import { Header } from './ui';

const ActionGroup = ({ canCreate = false }) => {
  return (
    <div className="flex flex-col md:flex-row md:justify-center gap-3">
      {canCreate && (
        <StaticIntegrationsDialog
          dialogTrigger={
            <Button size="sm">
              <Plus />
              Add integration
            </Button>
          }
        />
      )}
      <Button variant="outline" asChild size="sm">
        <Link to="https://codekeeper.co/integrations" target="_blank">
          Learn more
        </Link>
      </Button>
    </div>
  );
};

const Container = withSuspense(() => {
  const dataCompany = cache.readQuery<ICompanyQuery>({ query: CompanyDocument });
  const canReadIntegrations = dataCompany?.company.policy.readIntegrations;
  const { data } = useSuspenseQuery(IntegrationsDocument, {
    variables: {
      page: 1,
      perPage: 0,
    },
    skip: !canReadIntegrations,
  });

  if (!canReadIntegrations) return <Navigate to="/" />;

  if (data?.integrations.nodesCount)
    return (
      <div className="container mx-auto">
        <Header canCreate={dataCompany.company.policy.createIntegration} />
        <IntegrationTable />
      </div>
    );

  return (
    <div className="container mx-auto h-full">
      <EmptyBlock
        title="Set up your first integration"
        description="Connect Codekeeper with the tools you already use to deposit your assets and keep them up to date automatically."
        icon={LayoutGrid}
        action={<ActionGroup canCreate={dataCompany.company.policy.createIntegration} />}
        classes={{
          card: 'h-full [&>div]:h-full',
          title: 'text-base',
          content: 'h-full flex flex-col justify-center',
        }}
      />
    </div>
  );
});

const IntegrationsPage = () => {
  usePageMeta('Integrations');

  return (
    <Main title="Integrations" className="bg-background-secondary">
      <IntegrationContextProvider>
        <Container />
        <IntegrationSetupDialog />
        <DisconnectDialog />
        <UpdateCustomIntegrationDialog />
        <ReconnectOauthDialog />
        <SetupAlertDialog />
      </IntegrationContextProvider>
    </Main>
  );
};

export default IntegrationsPage;
